import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";
import { Form, Input, Select, Spin, Divider, Alert, DatePicker, Tag } from "antd";
import dayjs from "dayjs";
import ChantPopConfirm from "../../../../../CommonComponents/ChantTooltip/ChantPopConfirm";
import { fbGetPartners } from "../../../../../services/firebaseService/endPoints/admin/partners";
import { getLocations } from "../../../../../services/firebaseService/endPoints/admin/content";
import logger from "../../../../../utils/logger";

import ScreenHeader from "../../../../../CommonComponents/ScreenHeader";
import ImageUploadButton from "../../../website/ImageUploaderButton";
import ChantButton from "../../../../../CommonComponents/ChantButton/ChantButton";
import HelpTooltip from "../../../../../CommonComponents/HelpTooltip/HelpTooltip";
import ChantCheckbox from "../../../../../CommonComponents/ChantCheckbox/ChantCheckbox";
import GroupSelector from "../../../../../CommonComponents/GroupSelector/GroupSelector";
import PriceOptionsForm from "./PriceOptionsForm";
import useChildrenGroups from "../../../../clubAdmin/hooks/useChildrenGroups";

import { useGiveawayForm } from "./useGiveawayForm";
import { DEFAULT_PRICE_OPTIONS, MAX_WINNERS, FORM_STYLES } from "./constants";
import "./CreateGiveaway.css";
import { fbGetGiveaway } from "../../../../../services/firebaseService/endPoints/admin/content/giveaway";
import useDashboardInfo from "../../../../../hooks/useDashboardInfo";
import useCurrency from "../../../../../utils/hooks/useCurrency";

const CreateGiveaway = () => {
  const { giveawayId } = useParams();
  const [form] = Form.useForm();
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const { isParent } = useDashboardInfo();

  const [fetchGiveawayLoading, setFetchGiveawayLoading] = useState(false);

  const fetchGiveaway = async () => {
    try {
      setFetchGiveawayLoading(true);
      const giveaway = await fbGetGiveaway(groupData?.id, giveawayId, isParent);
      if (giveaway) {
        // Map Firebase fields to form fields
        const formValues = {
          itemName: giveaway.itemName,
          details: giveaway.eventDesc,
          noOfWinners: giveaway.quantity,
          allowFans: giveaway.allowFans,
          multipleWinnerSelection: giveaway.multipleWinnerSelection,
          offerPurchase: giveaway.offerPurchase,
          goalAmount: giveaway.goalAmount,
          allowMultiplePurchases: giveaway.moreThanOnePurchase,
          proceedsSupport: giveaway.partnerId,
          donatedByText: giveaway.donatedByText,
          closeDate: giveaway.closeDate
            ? dayjs.unix(giveaway.closeDate)
            : undefined,
          mustBePresentToWin: giveaway.mustBePresentToWin || false,
        };

        if (giveaway.locations?.length > 0) {
          setSelectedLocations(giveaway.locations);
        }

        // Set form values
        form.setFieldsValue(formValues);

        // Set price options if they exist
        if (
          giveaway.priceEntryOptions &&
          giveaway.priceEntryOptions.length > 0
        ) {
          setPriceOptions(giveaway.priceEntryOptions);
        }

        // Set number of winners state
        setNumberOfWinners(giveaway.quantity || 1);

        // Handle images - store URLs separately from files
        if (giveaway.itemImage && giveaway?.itemImage?.length > 0) {
          setItemImageUrl(giveaway.itemImage);
        }
        if (giveaway.donatedByImage && giveaway?.donatedByImage?.length > 0) {
          setDonatedByImageUrl(giveaway.donatedByImage);
        }

        // Load audience data if it exists
        if (giveaway.audience) {
          setSelectedGroups(giveaway.audience);
        }
      }
    } catch (error) {
      logger.error("Error fetching giveaway:", error);
    } finally {
      setFetchGiveawayLoading(false);
    }
  };

  useEffect(() => {
    if (giveawayId) {
      fetchGiveaway();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giveawayId]);

  // State
  const [partners, setPartners] = useState([]);
  const [locations, setLocations] = useState([]);
  const [numberOfWinners, setNumberOfWinners] = useState(1);
  const [itemImageFile, setItemImageFile] = useState(null);
  const [itemImageUrl, setItemImageUrl] = useState(null);
  const [donatedByImageFile, setDonatedByImageFile] = useState(null);
  const [donatedByImageUrl, setDonatedByImageUrl] = useState(null);
  const [priceOptions, setPriceOptions] = useState(DEFAULT_PRICE_OPTIONS);
  const [selectedLocations, setSelectedLocations] = useState([]);

  // Group selector for clubadmin
  const { childGroupDetails } = useChildrenGroups();
  const [selectedGroups, setSelectedGroups] = useState({
    selectionType: "all",
    selectedValues: null,
    groupIds: null,
  });

  const handleGroupSelectionChange = (groupSelection) => {
    setSelectedGroups({
      selectionType: groupSelection.selectionType || "all",
      selectedValues: groupSelection.selectedValues || null,
      groupIds: groupSelection.groupIds || null,
    });
  };

  const { submitLoading, handleSubmit, stripeError } = useGiveawayForm(form, selectedGroups);
  const groupCurrency = useCurrency();

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const partners = await fbGetPartners(groupData?.id);
        const partnerOptions = partners.map((partner) => ({
          label: partner.name,
          value: partner.id,
          key: partner.id,
        }));
        setPartners(partnerOptions);
      } catch (error) {
        logger.log("error", error);
      }
    };

    const fetchLocations = async () => {
      try {
        const locs = await getLocations(groupData?.id);
        const locationOptions = locs.map((loc) => ({
          label: loc.name,
          value: loc.id,
        }));
        setLocations(locationOptions);
      } catch (error) {
        logger.log("error fetching locations", error);
      }
    };

    if (groupData?.id) {
      fetchPartners();
      fetchLocations();
    }
  }, [groupData?.id]);

  const handleWinnersChange = (value) => {
    setNumberOfWinners(value);
    if (value === 1) {
      form.setFieldValue("multipleWinnerSelection", false);
    }
  };

  return (
    <div style={{ padding: "12px" }}>
      <ScreenHeader title="Create Giveaway" />

      <div className="admin-giveaway-form">
        {fetchGiveawayLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "85vh",
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <>
            {isParent && (
              <div style={{ marginBottom: "20px" }}>
                <GroupSelector
                  selectedGroups={selectedGroups}
                  allGroups={childGroupDetails}
                  onChange={handleGroupSelectionChange}
                  typeLabel="Who should see this giveaway?"
                />
                <Divider style={{ margin: "16px 0" }} />
              </div>
            )}

            {stripeError && (
              <Alert
                message={stripeError}
                type="error"
                showIcon
                style={{ marginBottom: "16px" }}
              />
            )}

            <Form layout="vertical" form={form} labelCol={FORM_STYLES.labelCol} onValuesChange={(changedValues) => {
                if ('mustBePresentToWin' in changedValues && !changedValues.mustBePresentToWin) {
                  setSelectedLocations([]);
                }
              }}>
              <Form.Item
                label="Item"
                name="itemName"
                rules={[{ required: true, message: "Item is required" }]}
              >
                <Input
                  placeholder="Enter item"
                  style={{ width: FORM_STYLES.inputWidth }}
                  maxLength={50}
                />
              </Form.Item>

              <Form.Item label="Details" name="details">
                <Input.TextArea
                  placeholder="Enter details"
                  style={{ width: FORM_STYLES.inputWidth }}
                  rows={4}
                  maxLength={150}
                />
              </Form.Item>

              <Form.Item label="Image" name="image">
                <ImageUploadButton
                  text="Upload"
                  images={
                    itemImageFile
                      ? [itemImageFile]
                      : itemImageUrl
                        ? [itemImageUrl]
                        : []
                  }
                  onSave={(images) => {
                    setItemImageFile(images[0]);
                    setItemImageUrl(null); // Clear URL when new file is uploaded
                  }}
                />
              </Form.Item>

              <Form.Item
                label="No. of Winners"
                name="noOfWinners"
                rules={[
                  { required: true, message: "Number of winners is required" },
                ]}
              >
                <Select
                  placeholder="Select number of winners"
                  style={{ width: FORM_STYLES.inputWidth }}
                  onChange={handleWinnersChange}
                >
                  {[...Array(MAX_WINNERS)].map((_, index) => (
                    <Select.Option key={index + 1} value={index + 1}>
                      {index + 1}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {groupData?.regCodeRole === "fan" && (
                <Form.Item
                  name="allowFans"
                  valuePropName="checked"
                  style={{ marginBottom: FORM_STYLES.marginBottom }}
                >
                  <ChantCheckbox>Allow fans to participate?</ChantCheckbox>
                </Form.Item>
              )}

              {numberOfWinners > 1 && (
                <Form.Item
                  name="multipleWinnerSelection"
                  valuePropName="checked"
                >
                  <ChantCheckbox>
                    Allow members to win more than once?
                  </ChantCheckbox>
                </Form.Item>
              )}

              <Form.Item
                name="offerPurchase"
                valuePropName="checked"
                style={{ marginBottom: FORM_STYLES.marginBottom }}
              >
                <ChantCheckbox>
                  <div
                    style={{
                      display: "flex",
                      gap: FORM_STYLES.flexGap,
                      alignItems: "center",
                    }}
                  >
                    Offer purchase of drawing entries?
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#999",
                        fontWeight: "normal",
                      }}
                    >
                      Stripe account required
                    </span>
                    <HelpTooltip
                      text={
                        <div>
                          <span>
                            Offering purchase of drawing entries should be done
                            in compliance with the local laws.
                          </span>
                          <br />
                          <span>
                            Add Partner (proceeds support) via the admin
                            dashboard in Content/Partners
                          </span>
                        </div>
                      }
                      color="black"
                    />
                  </div>
                </ChantCheckbox>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.offerPurchase !== currentValues.offerPurchase
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue("offerPurchase") ? (
                    <>
                      <Form.Item
                        label="Goal"
                        name="goalAmount"
                        rules={[
                          {
                            required: true,
                            message:
                              "Goal amount is required when offering purchases",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Enter goal amount"
                          style={{ width: FORM_STYLES.inputWidth }}
                          type="number"
                          min={0}
                          addonBefore={groupCurrency}
                          onWheel={(e) => e.target.blur()}
                        />
                      </Form.Item>

                      <PriceOptionsForm
                        priceOptions={priceOptions}
                        setPriceOptions={setPriceOptions}
                        form={form}
                        groupCurrency={groupCurrency}
                      />

                      <Form.Item
                        name="allowMultiplePurchases"
                        valuePropName="checked"
                        style={{ marginBottom: FORM_STYLES.marginBottom }}
                      >
                        <ChantCheckbox>
                          Allow members to make more than one purchase?
                        </ChantCheckbox>
                      </Form.Item>

                      <Form.Item
                        label="Proceeds Support"
                        name="proceedsSupport"
                        help="Add via Admin Dashboard/Partners"
                      >
                        <Select
                          placeholder="Select Partner"
                          style={{ width: FORM_STYLES.inputWidth }}
                          options={partners}
                          optionFilterProp="label"
                          fieldNames={{ label: "label", value: "value" }}
                        />
                      </Form.Item>
                    </>
                  ) : null
                }
              </Form.Item>

              <Form.Item label="Donated by">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100px",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ width: "max-content" }}>
                    <Form.Item
                      name="donatedByImage"
                      style={{ margin: 0 }}
                      noStyle={false}
                    >
                      <ImageUploadButton
                        text="Upload"
                        images={
                          donatedByImageFile
                            ? [donatedByImageFile]
                            : donatedByImageUrl
                              ? [donatedByImageUrl]
                              : []
                        }
                        onSave={(images) => {
                          setDonatedByImageFile(images[0]);
                          setDonatedByImageUrl(null); // Clear URL when new file is uploaded
                        }}
                      />
                    </Form.Item>
                  </div>

                  <div style={{ fontSize: "16px" }}>or</div>

                  <div>
                    <Form.Item
                      name="donatedByText"
                      style={{ margin: 0, width: "250px" }}
                      noStyle={false}
                    >
                      <Input placeholder="Enter name" />
                    </Form.Item>
                  </div>
                </div>
              </Form.Item>

              <Form.Item
                label="Close Date"
                name="closeDate"
                rules={[{ required: true, message: "Close date is required" }]}
                help="Date displayed on giveaway. Admin responsible for closing giveaway."
                style={{ marginBottom: "32px" }}
              >
                <DatePicker style={{ width: FORM_STYLES.inputWidth }} />
              </Form.Item>

              <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                <Form.Item
                  name="mustBePresentToWin"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <ChantCheckbox>Must be present to win?</ChantCheckbox>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.mustBePresentToWin !== currentValues.mustBePresentToWin
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue("mustBePresentToWin") ? (
                      <div style={{ marginTop: "8px", marginBottom: 0 }}>
                        <Select
                          placeholder={selectedLocations.length >= 10 ? "Maximum 10 locations reached" : "Select location"}
                          style={{ width: FORM_STYLES.inputWidth }}
                          options={locations.filter(
                            (loc) => !selectedLocations.includes(loc.value),
                          )}
                          value={null}
                          disabled={selectedLocations.length >= 10}
                          onChange={(value) => {
                            if (value && selectedLocations.length < 10) {
                              setSelectedLocations([...selectedLocations, value]);
                            }
                          }}
                        />
                        {selectedLocations.length > 0 && (
                          <div
                            style={{
                              marginTop: "10px",
                              padding: "10px 12px",
                              background: "#f5f5f5",
                              borderRadius: "8px",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "8px",
                              width: FORM_STYLES.inputWidth,
                            }}
                          >
                            {selectedLocations.map((locId) => {
                              const loc = locations.find(
                                (l) => l.value === locId,
                              );
                              return (
                                <Tag
                                  key={locId}
                                  closable
                                  onClose={() =>
                                    setSelectedLocations(
                                      selectedLocations.filter(
                                        (id) => id !== locId,
                                      ),
                                    )
                                  }
                                  style={{
                                    fontSize: "13px",
                                    padding: "3px 10px",
                                    borderRadius: "16px",
                                    margin: 0,
                                  }}
                                >
                                  {loc?.label || locId}
                                </Tag>
                              );
                            })}
                          </div>
                        )}
                        <div
                          style={{
                            marginTop: "4px",
                            fontSize: "12px",
                            color: "#888",
                          }}
                        >
                          If selected, location is checked upon giveaway entry, not when winner is selected
                        </div>
                      </div>
                    ) : null
                  }
                </Form.Item>
              </div>

              <Form.Item
                name="termsAndConditions"
                valuePropName="checked"
                style={{ marginBottom: FORM_STYLES.marginBottom }}
                rules={[
                  {
                    required: true,
                    message: "Please accept terms and conditions",
                  },
                ]}
              >
                <ChantCheckbox>
                  <span style={{ color: "red" }}>* </span>I understand and am in
                  compliance with all local, state and national laws governing
                  this giveaway.
                </ChantCheckbox>
              </Form.Item>

              <Form.Item
                style={{
                  marginTop: "24px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div style={{ display: "flex", gap: FORM_STYLES.flexGap }}>
                  {giveawayId ? (
                    <ChantButton
                      onClick={() => {
                        form.validateFields().then((values) => {
                          handleSubmit(
                            { ...values, locations: selectedLocations },
                            "update",
                            priceOptions,
                            itemImageFile || itemImageUrl,
                            donatedByImageFile || donatedByImageUrl,
                            giveawayId,
                          );
                        });
                      }}
                      loading={submitLoading["update"]}
                    >
                      Update
                    </ChantButton>
                  ) : (
                    <ChantButton
                      onClick={() => {
                        form.validateFields().then((values) => {
                          handleSubmit(
                            { ...values, locations: selectedLocations },
                            "save",
                            priceOptions,
                            itemImageFile || itemImageUrl,
                            donatedByImageFile || donatedByImageUrl,
                          );
                        });
                      }}
                      loading={submitLoading["save"]}
                    >
                      Save
                    </ChantButton>
                  )}

                  <ChantPopConfirm
                    title="Publish will make the giveaway live and send a notification."
                    onCancel={() => {}}
                    onConfirm={() => {
                      form.validateFields().then((values) => {
                        handleSubmit(
                          { ...values, locations: selectedLocations },
                          "publish",
                          priceOptions,
                          itemImageFile || itemImageUrl,
                          donatedByImageFile || donatedByImageUrl,
                          giveawayId,
                        );
                      });
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <ChantButton loading={submitLoading["publish"]}>
                      Publish
                    </ChantButton>
                  </ChantPopConfirm>
                </div>
              </Form.Item>
            </Form>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateGiveaway;
